import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  FlaskConical,
  Send,
  BarChart3,
  MoreHorizontal,
  ArrowLeft,
  Database,
  Mail,
  Workflow,
  Globe,
} from "lucide-react";
import { listAssistantPresets } from "@/api/assistant";
import { cn } from "@/cn";

const PRIMARY_CATEGORIES = [
  { id: "prospecting", label: "Lead discovery", icon: Search },
  { id: "research", label: "Research", icon: FlaskConical },
  { id: "sequencing", label: "Sequencing", icon: Send },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
] as const;

const MORE_CATEGORIES = [
  { id: "enrichment", label: "Enrichment", icon: Database },
  { id: "deliverability", label: "Deliverability", icon: Mail },
  { id: "workflows", label: "Workflows", icon: Workflow },
  { id: "web_search", label: "Web Search", icon: Globe },
] as const;

export function AssistantPresets({
  onSelect,
}: {
  onSelect: (preset: { id: string; name: string; text: string; autorun?: boolean }) => void;
}) {
  const { data } = useQuery({
    queryKey: ["assistant-presets"],
    queryFn: listAssistantPresets,
    staleTime: 60_000,
  });
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  const presets = data?.assistant_presets ?? [];

  const inCategory = useMemo(() => {
    if (!activeCategory) return [];
    return presets.filter(
      (p) => p.visibility !== false && p.category === activeCategory,
    );
  }, [presets, activeCategory]);

  if (activeCategory) {
    return (
      <div className="w-full max-w-2xl">
        <p className="mb-3 text-center text-sm text-muted-foreground">
          Start with a preset
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {inCategory.map((p) => (
            <button
              key={p.id}
              type="button"
              title={p.short_description}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-left text-sm text-foreground transition hover:border-brand/40 hover:bg-brand-soft/40"
              onClick={() =>
                onSelect({
                  id: p.id,
                  name: p.name,
                  text: p.text,
                  autorun: p.autorun,
                })
              }
            >
              {p.name}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="mx-auto mt-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => setActiveCategory(null)}
        >
          <ArrowLeft className="size-3.5" />
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl">
      <p className="mb-3 text-center text-sm text-muted-foreground">
        Start with a preset
      </p>
      <div className="relative flex flex-wrap justify-center gap-2">
        {PRIMARY_CATEGORIES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-brand/40 hover:bg-brand-soft/40"
            onClick={() => setActiveCategory(id)}
          >
            <Icon className="size-3.5 text-muted-foreground" />
            {label}
          </button>
        ))}
        <div className="relative">
          <button
            type="button"
            aria-expanded={moreOpen}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-brand/40 hover:bg-brand-soft/40",
              moreOpen && "border-brand/40 bg-brand-soft/40",
            )}
            onClick={() => setMoreOpen((o) => !o)}
          >
            <MoreHorizontal className="size-3.5 text-muted-foreground" />
            More
          </button>
          {moreOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40"
                aria-label="Close more menu"
                onClick={() => setMoreOpen(false)}
              />
              <div
                role="menu"
                className="absolute bottom-full left-1/2 z-50 mb-2 w-48 -translate-x-1/2 rounded-lg border border-border bg-card py-1 shadow-lg"
              >
                {MORE_CATEGORIES.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/20"
                    onClick={() => {
                      setMoreOpen(false);
                      setActiveCategory(id);
                    }}
                  >
                    <Icon className="size-3.5 text-muted-foreground" />
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
